import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { brandAlpha, cardInteractive, hoverLift, hoverOnly, iconTilePrimary, iconTileSecondary } from "../../../theme/mixins";

// -----------------------------
// Static Styles
// -----------------------------
export const headerBoxStyles: SxProps<Theme> = { mb: 4 };
export const titleStyles: SxProps<Theme> = { fontWeight: 700, letterSpacing: '-0.015em' };
export const subtitleStyles: SxProps<Theme> = { color: 'text.secondary' };
export const alertStyles: SxProps<Theme> = { mb: 3, borderRadius: 3 };
export const dashboardWrapperStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    gap: 3,
};
export const leftColumnStyles: SxProps<Theme> = { flex: 2 };
export const rightColumnStyles: SxProps<Theme> = { flex: 1 };
export const cardMarginBottom: SxProps<Theme> = { mb: 3 };
export const sectionTitleStyles: SxProps<Theme> = { fontWeight: 700, mb: 3, letterSpacing: '-0.01em' };
export const quickActionsGridStyles: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(250px, 100%), 1fr))',
    gap: 2,
};
export const quickActionInnerStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    mb: 2,
};
export const appointmentListBoxStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
};
export const appointmentCardContentPadding: SxProps<Theme> = { p: 2.5, paddingBottom: '20px !important' };
export const appointmentMainBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};
export const appointmentLeftBoxStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
};
export const appointmentTitleStyles: SxProps<Theme> = { fontWeight: 600 };
export const appointmentOrganizerStyles: SxProps<Theme> = { color: 'text.secondary' };
export const appointmentRightBoxStyles: SxProps<Theme> = { textAlign: 'right' };
export const loadingBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'center',
    py: 4,
};
export const emptyStateBoxStyles: SxProps<Theme> = { textAlign: 'center', py: 4 };
export const helpButtonTextStyles: SxProps<Theme> = {};
export const upcomingHeaderBoxStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 3,
};
export const statsCardStyles: SxProps<Theme> = { mb: 3 };
export const upcomingListStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
};
export const upcomingRowStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    flexWrap: 'wrap',
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(16,24,40,0.08)',
    backgroundColor: 'background.paper',
};
/** The "awaiting confirmation" chip beside a pending request's time. */
export const upcomingStatusChipStyles: SxProps<Theme> = {
    ml: 1,
};

export const upcomingWhenStyles: SxProps<Theme> = { fontWeight: 600 };
export const upcomingWithStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
};
export const activePlansListStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
};
export const activePlanRowStyles: SxProps<Theme> = {
    p: 2,
    borderRadius: 2,
    border: '1px solid rgba(16,24,40,0.08)',
    backgroundColor: 'background.paper',
};
export const activePlanHeaderRowStyles: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    flexWrap: 'wrap',
};
export const activePlanServiceNameStyles: SxProps<Theme> = { fontWeight: 600 };
export const activePlanTherapistStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.85rem',
};
export const activePlanRemainingStyles: SxProps<Theme> = { fontWeight: 700, mt: 1 };
export const activePlanScheduledStyles: SxProps<Theme> = {
    color: 'text.secondary',
    fontSize: '0.8rem',
    mb: 0.5,
};
export const activePlanProgressBarStyles: SxProps<Theme> = {
    height: 8,
    borderRadius: 4,
    mt: 0.5,
};
export const activePlanChipStyles: SxProps<Theme> = { mt: 1 };

// -----------------------------
// Theme-dependent Styles
// -----------------------------
export const quickActionCardStyles = (
    theme: Theme,
    color: string
): SxProps<Theme> => ({
    p: 2.5,
    cursor: 'pointer',
    ...cardInteractive,
    // Restates the lift it just spread in, because this replaces the mixin's hover block
    // rather than merging into it -- the card's own colour is the only intended change.
    ...hoverOnly({
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 32px rgba(16,24,40,0.10)',
        borderColor: color,
    }),
});

// Quick-action icons sit in the same soft tinted tile the marketing site uses.
export const quickActionIconTileStyles = (
    paletteKey: 'primary' | 'secondary' = 'primary'
): SxProps<Theme> => (paletteKey === 'secondary' ? iconTileSecondary : iconTilePrimary);

export const quickActionIconStyle = (
    theme: Theme,
    paletteKey: 'primary' | 'secondary' = 'primary'
): SxProps<Theme> => ({
    fontSize: 30,
});

export const calendarIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 64,
    color: theme.palette.grey[300],
    mb: 2,
});

export const planIconStyles = (theme: Theme): SxProps<Theme> => ({
    fontSize: 64,
    color: theme.palette.grey[300],
    mb: 2,
});

export const appointmentCardStyles = (theme: Theme): SxProps<Theme> => ({
    border: '1px solid rgba(16,24,40,0.06)',
    ...hoverLift,
    // As above: replaces rather than merges, so the lift is restated alongside the border
    // colour that is the actual addition.
    ...hoverOnly({
        transform: 'translateY(-4px)',
        boxShadow: '0 16px 32px rgba(16,24,40,0.10)',
        borderColor: brandAlpha('primary', 0.25),
    }),
});

export const avatarStyles = (theme: Theme): SxProps<Theme> => ({
    backgroundColor: theme.palette.primary.main,
    mr: 2,
    width: 48,
    height: 48,
    fontSize: '1.2rem',
    fontWeight: 700
});

export const progressIconStyles = (theme: Theme): SxProps<Theme> => ({
    color: theme.palette.success.main,
    mr: 1,
});

export const progressCountStyles = (theme: Theme): SxProps<Theme> => ({
    fontWeight: 700,
    color: theme.palette.primary.main,
});

// -----------------------------
// Utilities
// -----------------------------
export const getStatusColor = (theme: Theme, status?: string): string => {
    if (!status) return theme.palette.grey[500];
    switch (status.toLowerCase()) {
        case 'confirmed':
            return theme.palette.success.main;
        case 'pending':
            return theme.palette.warning.main;
        case 'cancelled':
        case 'canceled':
            return theme.palette.error.main;
        default:
            return theme.palette.grey[500];
    }
};

export const chipStyles = (theme: Theme, status?: string): SxProps<Theme> => ({
    backgroundColor: getStatusColor(theme, status),
    color: 'white',
    mt: 1,
});
